import { Component, OnInit } from '@angular/core';
import { KnowledgebaseService, KnowledgeEntry } from '../../../services/knowledgebase.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-knowledgebase',
  templateUrl: './knowledgebase.component.html',
  styleUrls: ['./knowledgebase.component.css']
})
export class KnowledgebaseComponent implements OnInit {
  entries: KnowledgeEntry[] = [];
  stats: any = { total_entries: 0, training_ready: 0, categories: [], demographics: [] };
  isLoading = false;

  // Filters & Pagination
  searchQuery = '';
  selectedCategory = 'all';
  selectedDemographic = 'all';
  selectedRegion = 'all';
  selectedStatus = 'all';
  selectedSort = 'newest';
  currentPage = 1;
  pageSize = 15;
  totalEntries = 0;
  totalPages = 1;

  // Edit Modal State
  isModalOpen = false;
  editingEntry: KnowledgeEntry | null = null;
  isSaving = false;

  constructor(private kbService: KnowledgebaseService) {}

  ngOnInit(): void {
    this.loadStats();
    this.loadEntries();
  }

  loadStats(): void {
    this.kbService.getStats().subscribe({
      next: (res) => {
        if (res.success) {
          this.stats = res.stats;
        }
      },
      error: (err) => console.error('Error fetching stats:', err)
    });
  }

  loadEntries(): void {
    this.isLoading = true;
    const params: any = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.searchQuery.trim()) params.search = this.searchQuery.trim();
    if (this.selectedCategory !== 'all') params.category = this.selectedCategory;
    if (this.selectedDemographic !== 'all') params.demographic = this.selectedDemographic;
    if (this.selectedRegion !== 'all') params.region = this.selectedRegion;
    if (this.selectedStatus !== 'all') params.is_approved = this.selectedStatus;
    if (this.selectedSort && this.selectedSort !== 'newest') params.sort_by = this.selectedSort;

    this.kbService.getList(params).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.success) {
          this.entries = res.data;
          this.totalEntries = res.pagination.total;
          this.totalPages = res.pagination.total_pages;
        }
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Error loading knowledgebase entries:', err);
      }
    });
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadEntries();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadEntries();
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadEntries();
    }
  }

  openEditModal(entry: KnowledgeEntry): void {
    // Clone entry to prevent direct mutation before saving
    this.editingEntry = { ...entry };
    this.isModalOpen = true;
  }

  closeModal(): void {
    this.isModalOpen = false;
    this.editingEntry = null;
  }

  saveEdit(): void {
    if (!this.editingEntry) return;

    this.isSaving = true;
    this.kbService.updateEntry(this.editingEntry.id, this.editingEntry).subscribe({
      next: (res) => {
        this.isSaving = false;
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Updated!',
            text: 'Knowledgebase entry saved successfully.',
            timer: 1800,
            showConfirmButton: false
          });
          this.closeModal();
          this.loadEntries();
          this.loadStats();
        } else {
          Swal.fire('Error', res.message || 'Failed to update entry', 'error');
        }
      },
      error: (err) => {
        this.isSaving = false;
        Swal.fire('Error', 'An unexpected error occurred while saving', 'error');
      }
    });
  }

  toggleApproval(entry: KnowledgeEntry): void {
    const updatedStatus = entry.is_approved ? 0 : 1;
    this.kbService.updateEntry(entry.id, { is_approved: updatedStatus, training_ready: updatedStatus }).subscribe({
      next: (res) => {
        if (res.success) {
          entry.is_approved = updatedStatus;
          entry.training_ready = updatedStatus;
          this.loadStats();
        }
      },
      error: (err) => console.error('Toggle error:', err)
    });
  }

  deleteEntry(entry: KnowledgeEntry): void {
    Swal.fire({
      title: 'Delete this entry?',
      text: `Are you sure you want to delete dataset row #${entry.id}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it'
    }).then((result) => {
      if (result.isConfirmed) {
        this.kbService.deleteEntry(entry.id).subscribe({
          next: (res) => {
            if (res.success) {
              Swal.fire({
                icon: 'success',
                title: 'Deleted!',
                text: 'The entry has been removed.',
                timer: 1500,
                showConfirmButton: false
              });
              this.loadEntries();
              this.loadStats();
            } else {
              Swal.fire('Error', res.message || 'Failed to delete entry', 'error');
            }
          },
          error: () => Swal.fire('Error', 'Failed to communicate with server', 'error')
        });
      }
    });
  }

  exportDataset(): void {
    this.kbService.exportDataset().subscribe({
      next: (res) => {
        if (res.success) {
          const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(res, null, 2));
          const downloadAnchor = document.createElement('a');
          downloadAnchor.setAttribute('href', dataStr);
          downloadAnchor.setAttribute('download', `joy_ai_training_dataset_${new Date().toISOString().slice(0, 10)}.json`);
          document.body.appendChild(downloadAnchor);
          downloadAnchor.click();
          downloadAnchor.remove();

          Swal.fire({
            icon: 'success',
            title: 'Dataset Exported',
            text: `Exported ${res.metadata?.total_training_samples || 0} training samples as JSON.`,
            timer: 2000,
            showConfirmButton: false
          });
        }
      },
      error: (err) => {
        Swal.fire('Error', 'Failed to export dataset', 'error');
      }
    });
  }

  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'academic_stress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'workplace_burnout':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'loneliness_isolation':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'imposter_syndrome':
        return 'bg-rose-100 text-rose-800 border-rose-200';
      case 'taboo_faq':
        return 'bg-teal-100 text-teal-800 border-teal-200';
      default:
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    }
  }

  formatCategoryLabel(category: string): string {
    return category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  formatDemographicLabel(demo: string): string {
    if (!demo) return 'University Student';
    if (demo === 'full_time_student' || demo === 'university_student') return 'Full-time Student';
    if (demo === 'student_part_time_job') return 'Student + Part-time Job';
    if (demo === 'working_full_time_student') return 'Working Full-time Student';
    return demo.replace(/_/g, ' ');
  }

  extractInstitution(context?: string): string {
    if (!context) return '';
    const m = context.match(/Institution:\s*([^.]+)\./i);
    return m ? m[1].trim() : '';
  }

  extractRegion(context?: string): string {
    if (!context) return '';
    const m = context.match(/Region:\s*([^.]+)\./i);
    return m ? m[1].trim() : '';
  }
}
